import { useEffect, useRef, useState } from 'react';
import { api, ApiError, uploadToPresignedUrl } from '../../lib/api';
import { Card, Empty, Notice, Spinner } from '../../components/Shell';
import { Stamp } from '../../components/Stamp';
import { SelectField, TextField } from '../../components/Field';
import { longDate } from '../../lib/format';
import type { PetDocument, PetDocumentType, VaccinationStatus } from '../../lib/types';

const ACCEPTED = 'application/pdf,image/jpeg,image/png,image/heic,image/webp';
const MAX_BYTES = 15 * 1024 * 1024;

const TYPE_LABELS: Record<PetDocumentType, string> = {
  vaccination: 'Vaccination record',
  medical: 'Medical record',
  medication: 'Medication instructions',
  insurance: 'Insurance',
  photo: 'Photo',
  other: 'Other',
};

/**
 * Vaccine and medical record uploads.
 *
 * Two steps against the API: ask for a presigned URL, PUT the file straight to
 * S3, then confirm. The file never passes through the API, which is what makes
 * a 12MB scan of a vaccine card work at all.
 */
export function PetDocuments({
  tenantId,
  petId,
  petName,
}: {
  tenantId: string;
  petId: string;
  petName: string;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<PetDocument[]>([]);
  const [vaccinations, setVaccinations] = useState<VaccinationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [type, setType] = useState<PetDocumentType>('vaccination');
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const refresh = () =>
    api<{ documents: PetDocument[]; vaccinations: VaccinationStatus }>(
      `/tenants/${tenantId}/pets/${petId}/documents`
    )
      .then((result) => {
        setDocuments(result.documents);
        setVaccinations(result.vaccinations);
        setError(null);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Records could not be loaded.'))
      .finally(() => setLoading(false));

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, petId]);

  const chooseFile = (file: File | null) => {
    setFieldErrors({});
    if (!file) {
      setPendingFile(null);
      return;
    }
    if (file.size > MAX_BYTES) {
      setFieldErrors({ file: 'That file is over 15MB. Try a photo instead of a scan.' });
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
    if (!label) setLabel(file.name.replace(/\.[^.]+$/, '').slice(0, 60));
  };

  const upload = (event: React.FormEvent) => {
    event.preventDefault();
    if (!pendingFile) return;

    if (type === 'vaccination' && !expiresAt) {
      setFieldErrors({ expiresAt: 'Enter the date this vaccination is due again.' });
      return;
    }

    setUploading(true);
    setFieldErrors({});

    void (async () => {
      try {
        const contentType = pendingFile.type || 'application/octet-stream';

        const ticket = await api<{ documentId: string; uploadUrl: string }>(
          `/tenants/${tenantId}/pets/${petId}/documents/upload-url`,
          {
            method: 'POST',
            body: { fileName: pendingFile.name, contentType, sizeBytes: pendingFile.size },
          }
        );

        await uploadToPresignedUrl(ticket.uploadUrl, pendingFile, contentType);

        await api(`/tenants/${tenantId}/pets/${petId}/documents`, {
          method: 'POST',
          body: {
            documentId: ticket.documentId,
            fileName: pendingFile.name,
            contentType,
            sizeBytes: pendingFile.size,
            type,
            label: label || undefined,
            expiresAt: expiresAt || undefined,
          },
        });

        setPendingFile(null);
        setLabel('');
        setExpiresAt('');
        if (fileInput.current) fileInput.current.value = '';
        await refresh();
      } catch (err) {
        if (err instanceof ApiError) {
          setFieldErrors(err.fieldErrors ?? {});
          if (!err.fieldErrors) setError(err.message);
        } else {
          setError('The upload did not finish. Try again.');
        }
      } finally {
        setUploading(false);
      }
    })();
  };

  const remove = (documentId: string) => {
    void api(`/tenants/${tenantId}/pets/${petId}/documents/${documentId}`, { method: 'DELETE' })
      .then(refresh)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That record could not be removed.'));
  };

  return (
    <section>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl">Records for {petName}</h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-soft)]">
            Vaccination cards, vet notes, medication instructions. A clear photo is fine.
          </p>
        </div>
        {vaccinations &&
          (vaccinations.current ? (
            <Stamp tone="ok" flat>
              Vaccines current
            </Stamp>
          ) : (
            <Stamp tone="flag" flat>
              Vaccines needed
            </Stamp>
          ))}
      </div>

      {error && (
        <div className="mb-6">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {vaccinations && vaccinations.expiringSoon.length > 0 && (
        <div className="mb-6">
          <Notice tone="warn" title="Due again soon">
            {vaccinations.expiringSoon.map((doc) => (
              <p key={doc.documentId}>
                {doc.label ?? doc.fileName} expires {doc.expiresAt}. Upload the new card when you have it.
              </p>
            ))}
          </Notice>
        </div>
      )}

      <Card className="p-6">
        <form onSubmit={upload} className="space-y-5">
          <div>
            <span className="field-label">Choose a file</span>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPTED}
              onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm file:mr-3 file:cursor-pointer file:rounded-sm file:border file:border-[color:var(--color-ink)] file:bg-transparent file:px-3 file:py-1.5 file:font-sans file:text-sm file:font-bold"
            />
            {fieldErrors.file && <span className="field-error">{fieldErrors.file}</span>}
          </div>

          {pendingFile && (
            <>
              <div className="grid gap-5 sm:grid-cols-2">
                <SelectField
                  label="What is it"
                  value={type}
                  onChange={(value) => setType(value as PetDocumentType)}
                  options={Object.entries(TYPE_LABELS).map(([value, text]) => ({ value, label: text }))}
                />
                <TextField
                  label="Label"
                  value={label}
                  onChange={setLabel}
                  placeholder="Rabies, DHPP, Bordetella"
                  error={fieldErrors.label}
                />
              </div>

              {(type === 'vaccination' || type === 'insurance') && (
                <TextField
                  label={type === 'vaccination' ? 'Due again on' : 'Expires'}
                  type="date"
                  value={expiresAt}
                  onChange={setExpiresAt}
                  error={fieldErrors.expiresAt}
                  hint="We will tell you a month before it lapses."
                  required={type === 'vaccination'}
                />
              )}

              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? 'Uploading' : 'Upload record'}
              </button>
            </>
          )}
        </form>
      </Card>

      <div className="mt-6">
        {loading ? (
          <Spinner label="Loading records" />
        ) : documents.length === 0 ? (
          <Empty
            title="No records yet"
            body={`${petName} needs a current vaccination record on file before a stay can be confirmed. A photo of the card is fine.`}
          />
        ) : (
          <ul className="space-y-2">
            {documents.map((doc) => (
              <li
                key={doc.documentId}
                className="card flex flex-wrap items-center justify-between gap-4 p-4"
              >
                <div className="min-w-0">
                  <p className="font-semibold">
                    {doc.label ?? doc.fileName}
                    <span className="ml-2 font-mono text-xs font-normal text-[color:var(--color-ink-faint)]">
                      {TYPE_LABELS[doc.type]}
                    </span>
                  </p>
                  <p className="typed text-xs text-[color:var(--color-ink-soft)]">
                    Added {longDate(doc.uploadedAt)}
                    {doc.expiresAt && ` · due again ${doc.expiresAt}`}
                  </p>
                  {doc.reviewStatus === 'rejected' && doc.reviewNote && (
                    <p className="mt-1 text-xs text-[color:var(--color-flag)]">{doc.reviewNote}</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  <ReviewStamp status={doc.reviewStatus} />
                  {doc.viewUrl && (
                    <a
                      className="btn btn-quiet"
                      href={doc.viewUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      View
                    </a>
                  )}
                  {doc.reviewStatus !== 'accepted' && (
                    <button type="button" className="btn btn-quiet" onClick={() => remove(doc.documentId)}>
                      Remove
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ReviewStamp({ status }: { status: PetDocument['reviewStatus'] }) {
  if (status === 'accepted') {
    return (
      <Stamp tone="ok" flat>
        Accepted
      </Stamp>
    );
  }
  if (status === 'rejected') {
    return (
      <Stamp tone="flag" flat>
        Needs a new copy
      </Stamp>
    );
  }
  return (
    <Stamp tone="quiet" flat>
      Awaiting review
    </Stamp>
  );
}
